import React from 'react'
import styled from '@emotion/styled'
import Layout from '../components/Layout.js'
import Container from '../components/Container.js'
import PageTitle from '../components/PageTitle.js'
import ContainerHero from '../components/ContainerHero.js'
import ContainerContent from '../components/ContainerContent.js'
import HeroLeftStandard from '../components/HeroLeftStandard.js'
import HeroCenterStandard from '../components/HeroCenterStandard.js'
import SEO from '../components/SEO.js'
import Newsletter from '../components/NewsletterPopup.js'
import Helmet from 'react-helmet'

// caly_faqData: FAQ-Inhalte extendIT GmbH (Calysto Marketing Solutions)
const caly_faqData = [
  {
    question: 'Kann ich Support erhalten, auch wenn ich noch kein Kunde bin?',
    answer: 'Ja. Sie können jederzeit Support anfragen – auch ohne bestehende Zusammenarbeit. Viele Kunden arbeiten mit Serviceverträgen, aber wir helfen auch bei einzelnen Anfragen.',
  },
  {
    question: 'Welche Modelle bieten Sie für IT-Betreuung an?',
    answer: 'Sie haben drei Möglichkeiten: Servicevertrag für laufende Betreuung, Stundenpakete mit fixem Kontingent oder Support nach Bedarf mit Einzelabrechnung.',
  },
  {
    question: 'Kann ich einfach vorbeikommen?',
    answer: 'Nein. Unsere Techniker arbeiten meist direkt bei Kunden oder in Projekten. Termine vergeben wir nur nach vorheriger Abstimmung.',
  },
  {
    question: 'Ist ein Erstgespräch kostenpflichtig?',
    answer: 'Nein. Das erste Gespräch ist kostenlos und unverbindlich. Wenn wir danach ein Konzept oder eine konkrete IT-Struktur ausarbeiten, verrechnen wir das. Das klären wir immer vorab.',
  },
  {
    question: 'Betreuen Sie Mac und Windows?',
    answer: 'Ja. Wir arbeiten mit beiden Systemen. Auch bestehende Linux-Umgebungen können wir betreuen.',
  },
  {
    question: 'Wie schnell sind die Reaktionszeiten im Support?',
    answer: 'Das hängt vom gewählten Modell ab. Kunden mit Servicevertrag haben Priorität. In den meisten Fällen kümmern wir uns noch am selben Tag.',
  },
  {
    question: 'Bieten Sie auch Remote-Support an?',
    answer: 'Ja. Viele Probleme lösen wir direkt per Fernwartung. Das spart Zeit und Kosten.',
  },
  {
    question: 'Übernehmen Sie die komplette IT-Betreuung für Unternehmen?',
    answer: 'Ja. Wir kümmern uns um die gesamte IT – von Arbeitsplätzen über Server bis zur laufenden Betreuung.',
  },
  {
    question: 'Unterstützen Sie auch bei IT-Projekten oder Umstellungen?',
    answer: 'Ja. Wir begleiten Projekte wie Systemumstellungen, Migrationen oder den Aufbau neuer IT-Infrastrukturen.',
  },
  {
    question: 'Kümmern Sie sich auch um Datensicherung und IT-Sicherheit?',
    answer: 'Ja. Wir richten Backups ein, prüfen bestehende Systeme und sorgen dafür, dass Ihre Daten geschützt sind.',
  },
  {
    question: 'Für wen ist Ihr Service gedacht?',
    answer: 'Für Unternehmen, die ihre IT zuverlässig betreiben wollen – ohne eigene IT-Abteilung oder als Ergänzung dazu.',
  },
]

// caly_faqSchema: FAQ JSON-LD Markup für Google Rich Results (Calysto Marketing Solutions)
const caly_faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: caly_faqData.map(item => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
}

// Zweispaltiges Layout: linke leere Spalte (350px) mit border-right,
// rechts der Content — identisch zur Footer-Struktur (FooterColumn1: flex 0 0 350px)
const Caly_FaqRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  border-top: 2px solid #000;
`

const Caly_FaqSideBar = styled.div`
  flex: 0 0 350px;
  max-width: 350px;
  border-right: 2px solid #000;

  @media (max-width: ${props => props.theme.responsive.largeMax}) {
    flex: 0 0 270px;
    max-width: 270px;
  }

  @media (max-width: ${props => props.theme.responsive.mediumMax}) {
    display: none;
  }
`

const Caly_FaqMain = styled.div`
  flex: 1;
  padding: 30px 15px;

  @media (min-width: ${props => props.theme.responsive.medium}) {
    padding: 60px;
  }
`

// XD-Spec: 120px/110px Telegraf Regular, margin-bottom 85px
const Caly_SubTitle = styled.h2`
  font-family: 'Telegraf', sans-serif;
  font-weight: 400 !important;
  font-size: 120px;
  line-height: 110px;
  letter-spacing: 0px;
  color: #000000;
  margin: 0 0 85px 0;

  @media (max-width: ${props => props.theme.responsive.largeMax}) {
    font-size: 72px;
    line-height: 72px;
    margin-bottom: 60px;
  }
  @media (max-width: ${props => props.theme.responsive.mediumMax}) {
    font-size: 48px;
    line-height: 52px;
    margin-bottom: 40px;
  }
  @media (max-width: ${props => props.theme.responsive.smallMax}) {
    font-size: 36px;
    line-height: 40px;
    margin-bottom: 30px;
  }
`

const Caly_FaqList = styled.div`
  width: 100%;
`

// Kein border zwischen Items — nur margin
const Caly_FaqItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 60px;

  @media (max-width: ${props => props.theme.responsive.mediumMax}) {
    margin-bottom: 40px;
    gap: 16px;
  }
  @media (max-width: ${props => props.theme.responsive.smallMax}) {
    margin-bottom: 28px;
    gap: 12px;
  }
`

// XD-Spec: Ellipse 42x42px, border 2px solid #000, Nummer zentriert
const Caly_FaqIcon = styled.div`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border: 2px solid #000000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Telegraf', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  color: #000000;
  margin-top: 8px;

  @media (max-width: ${props => props.theme.responsive.mediumMax}) {
    width: 34px;
    height: 34px;
    font-size: 12px;
    margin-top: 5px;
  }
  @media (max-width: ${props => props.theme.responsive.smallMax}) {
    width: 28px;
    height: 28px;
    font-size: 11px;
    margin-top: 3px;
  }
`

const Caly_FaqContent = styled.div`
  flex: 1;
`

// XD-Spec: 50px/60px Telegraf Regular
const Caly_FaqQuestion = styled.h3`
  font-family: 'Telegraf', sans-serif !important;
  font-weight: 400 !important;
  font-size: 50px !important;
  line-height: 60px !important;
  letter-spacing: 0px;
  color: #000000;
  margin: 0 0 20px 0 !important;

  @media (max-width: ${props => props.theme.responsive.largeMax}) {
    font-size: 36px !important;
    line-height: 44px !important;
  }
  @media (max-width: ${props => props.theme.responsive.mediumMax}) {
    font-size: 26px !important;
    line-height: 34px !important;
  }
  @media (max-width: ${props => props.theme.responsive.smallMax}) {
    font-size: 20px !important;
    line-height: 28px !important;
    margin-bottom: 12px !important;
  }
`

// XD-Spec: 18px/30px Telegraf Regular
const Caly_FaqAnswer = styled.p`
  font-family: 'Telegraf', sans-serif !important;
  font-weight: 400 !important;
  font-size: 18px !important;
  line-height: 30px !important;
  letter-spacing: 0px;
  color: #000000;
  margin: 0 !important;
  max-width: 827px;
  white-space: normal !important;

  @media (max-width: ${props => props.theme.responsive.mediumMax}) {
    font-size: 16px !important;
    line-height: 26px !important;
  }
`

const FaqPage = () => {
  return (
    <Layout>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(caly_faqSchema)}
        </script>
      </Helmet>
      <Newsletter />
      <SEO
        title="FAQ – Häufige Fragen zur IT-Betreuung"
        description="Antworten auf die häufigsten Fragen rund um IT-Support, Betreuungsmodelle, Remote-Service und mehr. extendIT GmbH, Wien."
      />
      <Container>
        <ContainerHero>
          <HeroLeftStandard>
            <PageTitle>FAQ</PageTitle>
          </HeroLeftStandard>
          <HeroCenterStandard>
            Häufig gestellte Fragen rund um IT-Support, Betreuungsmodelle,
            Remote-Service und mehr.
          </HeroCenterStandard>
        </ContainerHero>
        <ContainerContent>
          {/* Caly_FaqRow: border-top + zweispaltig wie Footer (350px Sidebar | Content) */}
          <Caly_FaqRow>
            <Caly_FaqSideBar />
            <Caly_FaqMain>
              <Caly_SubTitle>Häufig gestellte Fragen</Caly_SubTitle>
              <Caly_FaqList>
                {caly_faqData.map((item, index) => (
                  <Caly_FaqItem key={index}>
                    <Caly_FaqIcon>
                      {String(index + 1).padStart(2, '0')}
                    </Caly_FaqIcon>
                    <Caly_FaqContent>
                      <Caly_FaqQuestion>{item.question}</Caly_FaqQuestion>
                      <Caly_FaqAnswer>{item.answer}</Caly_FaqAnswer>
                    </Caly_FaqContent>
                  </Caly_FaqItem>
                ))}
              </Caly_FaqList>
            </Caly_FaqMain>
          </Caly_FaqRow>
        </ContainerContent>
      </Container>
    </Layout>
  )
}

export default FaqPage